import { useState, useEffect, useRef, useCallback } from "react";
import { C } from "./utils";

const API    = "https://cip.medsphere.net/api";
const WS_URL = "wss://cip.medsphere.net:8082";

let _ws       = null;
let _retryTm  = null;
let _listeners = []; // callbacks from mounted BellButton instances

const wsConnect = (userid) => {
  if(!userid) return;
  // if already open or actively connecting — skip
  if(_ws && (_ws.readyState===WebSocket.OPEN || _ws.readyState===WebSocket.CONNECTING)) return;
  // clear any stale closed socket
  if(_ws) { _ws.onclose=null; _ws.onerror=null; _ws=null; }
  clearTimeout(_retryTm);
  try {
    _ws = new WebSocket(WS_URL);
    _ws.onopen = () => {
      _ws.send(JSON.stringify({uniqueid:`notify_${userid}`}));
    };
    _ws.onmessage = (e) => {
      try {
        const raw  = JSON.parse(e.data);
        // server wraps event under "message" key
        const data = raw.message || raw;
        _listeners.forEach(fn=>fn(data));
      } catch{}
    };
    _ws.onclose = () => {
      _ws = null;
      _retryTm = setTimeout(()=>wsConnect(userid), 5000);
    };
    _ws.onerror = ()=>{ _ws?.close(); };
  } catch{}
};

const wsDisconnect = () => {
  clearTimeout(_retryTm);
  if(_ws){ _ws.onclose=null; _ws.close(); _ws=null; }
};

// ── Navigate based on notification fields ─────────────────────────────────────
function openNotif(n, activeSub) {
  const base = window.location.origin + window.location.pathname;
  const sub  = activeSub?.SUBPROJECTID || "";

  if(n.CONGRESSID) {
    const qs = new URLSearchParams({ congressid: n.CONGRESSID, sub });
    if(n.DOCUMENTID)     qs.set("doc", n.DOCUMENTID);
    if(n.DOCTYPEID)      qs.set("dt",  n.DOCTYPEID);
    if(n.PRESENTATIONID) qs.set("presentationid", n.PRESENTATIONID);
    if(n.COMMENTID)      qs.set("commentid", n.COMMENTID);
    window.open(`${base}?congress=1&${qs}`, `pvdoc_congress_${n.CONGRESSID}`, "noopener,noreferrer");
  } else if(n.DOCUMENTID && n.DOCTYPEID) {
    const qs = new URLSearchParams({ doc: n.DOCUMENTID, dt: n.DOCTYPEID, sub });
    if(n.COMMENTID) qs.set("commentid", n.COMMENTID);
    // named target — reuses existing tab if same document is already open
    window.open(`${base}?${qs}`, `pvdoc_${n.DOCUMENTID}`, "noopener,noreferrer");
  }
}

// ── Single notification row ───────────────────────────────────────────────────
function NotifRow({ n, activeSub, color, onMarkRead }) {
  const unread = n.STATUS === 1; // STATUS=1 = UNREAD, STATUS=0 = READ

  return(
    <div
      onClick={()=>{ openNotif(n, activeSub); if(unread) onMarkRead(n.NOTIFICATIONID); }}
      style={{
        display:"flex", gap:"10px", padding:"12px 16px", cursor:"pointer",
        background: unread ? `${color}08` : "#fff",
        borderBottom:`1px solid #F1F5F9`,
        borderLeft: unread ? `3px solid ${color}` : "3px solid transparent",
        transition:"background .1s"
      }}
      onMouseEnter={e=>e.currentTarget.style.background=unread?`${color}15`:"#F8FAFC"}
      onMouseLeave={e=>e.currentTarget.style.background=unread?`${color}08`:"#fff"}>

      {/* Avatar */}
      <div style={{
        width:"34px", height:"34px", borderRadius:"50%", flexShrink:0,
        background: unread ? color : "#94A3B8",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:"13px", fontWeight:"700", color:"#fff"
      }}>
        {n.NAME ? n.NAME[0].toUpperCase() : "·"}
      </div>

      {/* Content */}
      <div style={{flex:1, minWidth:0}}>
        <div style={{
          fontSize:"13px", fontWeight: unread ? "600" : "400",
          color: unread ? "#0F172A" : C.text,
          lineHeight:1.4, marginBottom:"3px",
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"
        }}>
          {n.TITLE}
        </div>
        <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
          {n.NAME && <span style={{fontSize:"11px", color:C.muted}}>{n.NAME}</span>}
          {n.NAME && <span style={{fontSize:"10px", color:C.hint}}>·</span>}
          <span style={{fontSize:"11px", color:C.hint}}>{n.TIME}</span>
        </div>
      </div>

      {/* Unread dot */}
      {unread && (
        <div style={{
          width:"8px", height:"8px", borderRadius:"50%",
          background:color, flexShrink:0, marginTop:"5px"
        }}/>
      )}
    </div>
  );
}

// ── Notification panel ─────────────────────────────────────────────────────────
export function NotificationPanel({ user, activeSub, color, onClose }) {
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("all"); // all | unread
  const panelRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    if(!user?.USERID || !activeSub?.SUBPROJECTID) return;
    try {
      const r = await fetch(
        `${API}/notification_history?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
        { headers:{"Auth-Code": user.AUTH_CODE} }
      );
      const res = await r.json();
      if(res.STATUS==="1" && Array.isArray(res.DATA)) setNotifs(res.DATA);
    } catch{}
    setLoading(false);
  }, [user, activeSub]);

  // Initial load + socket event refresh
  useEffect(()=>{
    fetchNotifs();
    const onMsg = () => fetchNotifs();
    window.addEventListener("pvision-notification", onMsg);
    return()=>{ window.removeEventListener("pvision-notification", onMsg); };
  },[fetchNotifs]);

  // Close on outside click
  useEffect(()=>{
    const h = e => { if(panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return()=>document.removeEventListener("mousedown", h);
  },[onClose]);

  const markRead = (id) => setNotifs(prev=>prev.map(n=>n.NOTIFICATIONID===id?{...n,STATUS:0}:n));

  const markAllRead = async () => {
    if(!user?.USERID||!activeSub?.SUBPROJECTID) return;
    setNotifs(prev=>prev.map(n=>({...n,STATUS:0})));
    try {
      const fd = new FormData();
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub.SUBPROJECTID);
      await fetch(`${API}/mark_all_read`,{method:"POST",
        headers:{"Auth-Code":user.AUTH_CODE},body:fd});
    } catch{}
  };

  const unreadCount = notifs.filter(n=>n.STATUS===1).length;
  const displayed   = tab==="unread" ? notifs.filter(n=>n.STATUS===1) : notifs;

  return(
    <div ref={panelRef} style={{
      position:"fixed", top:"52px", right:"50px",
      width:"380px", maxHeight:"520px", zIndex:600,
      background:"#fff", borderRadius:"12px",
      boxShadow:"0 8px 40px rgba(0,0,0,.15)",
      border:`1px solid ${C.border}`,
      display:"flex", flexDirection:"column", overflow:"hidden"
    }}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",padding:"14px 16px",
        borderBottom:`1px solid #F1F5F9`,flexShrink:0}}>
        <div style={{fontSize:"15px",fontWeight:"700",color:"#0F172A",flex:1}}>
          Notifications
          {unreadCount>0&&(
            <span style={{marginLeft:"8px",background:color,color:"#fff",
              fontSize:"11px",fontWeight:"700",padding:"2px 7px",
              borderRadius:"20px"}}>{unreadCount} new</span>
          )}
        </div>
        {unreadCount>0&&(
          <button onClick={markAllRead}
            style={{fontSize:"11px",color:color,background:"none",border:"none",
              cursor:"pointer",fontFamily:"inherit",fontWeight:"600",
              marginRight:"8px",padding:"4px 8px",borderRadius:"6px",
              transition:"background .12s"}}
            onMouseEnter={e=>e.currentTarget.style.background=`${color}12`}
            onMouseLeave={e=>e.currentTarget.style.background="none"}>
            Mark all read
          </button>
        )}
        <button onClick={onClose}
          style={{width:"26px",height:"26px",borderRadius:"50%",border:"none",
            background:"#F1F5F9",color:"#64748B",cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px"}}>
          ×
        </button>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",background:"#F8FAFC",
        borderBottom:`1px solid #F1F5F9`,flexShrink:0}}>
        {[
          {id:"all",    label:"All"},
          {id:"unread", label:`Unread${unreadCount>0?` (${unreadCount})`:""}`},
        ].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{flex:1,padding:"9px",border:"none",background:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:"12px",fontWeight:tab===t.id?"700":"400",
              color:tab===t.id?color:C.muted,
              borderBottom:`2px solid ${tab===t.id?color:"transparent"}`,
              transition:"all .12s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{flex:1,overflowY:"auto"}}>
        {loading&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            padding:"40px",color:C.muted,gap:"8px",fontSize:"13px"}}>
            <div style={{width:"14px",height:"14px",border:`2px solid #E2E8F0`,
              borderTopColor:color,borderRadius:"50%",
              animation:"spin .8s linear infinite"}}/>
            Loading…
            <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
          </div>
        )}
        {!loading&&displayed.length===0&&(
          <div style={{textAlign:"center",padding:"40px 20px",color:C.muted}}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke={C.border} strokeWidth="1.5" style={{marginBottom:"10px",display:"block",margin:"0 auto 10px"}}>
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            <div style={{fontSize:"13px"}}>
              {tab==="unread"?"No unread notifications":"No notifications"}
            </div>
          </div>
        )}
        {!loading&&displayed.map(n=>(
          <NotifRow key={n.NOTIFICATIONID} n={n}
            activeSub={activeSub} color={color} onMarkRead={markRead}/>
        ))}
      </div>

      {/* Footer */}
      <div style={{flexShrink:0,padding:"8px 16px",borderTop:`1px solid #F1F5F9`,
        display:"flex",justifyContent:"center"}}>
        <button onClick={()=>{ setLoading(true); fetchNotifs(); }}
          style={{fontSize:"11px",color:C.muted,background:"none",border:"none",
            cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:"4px"}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M23 4v6h-6M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
          </svg>
          Refresh
        </button>
      </div>
    </div>
  );
}

// ── Bell button with live unread count ───────────────────────────────────────
export function BellButton({ user, activeSub, color }) {
  const [open,   setOpen]   = useState(false);
  const cacheKey = `pv_notif_count_${user?.USERID}_${activeSub?.SUBPROJECTID}`;
  const [unread, setUnread] = useState(()=>{
    // show cached count instantly — no delay on mount
    try { return parseInt(localStorage.getItem(cacheKey)||"0",10)||0; } catch{ return 0; }
  });
  const userRef  = useRef(user);
  const subRef   = useRef(activeSub);
  useEffect(()=>{ userRef.current=user; subRef.current=activeSub;
    // reconnect if user just became available and WS isn't open
    if(user?.USERID) wsConnect(user.USERID);
    // refetch count when subproject changes
    if(user?.USERID && activeSub?.SUBPROJECTID) fetchCount();
  },[user?.USERID, activeSub?.SUBPROJECTID]);

  const fetchCount = async () => {
    const u=userRef.current; const s=subRef.current;
    if(!u?.USERID||!s?.SUBPROJECTID) return;
    try {
      const r = await fetch(
        `${API}/notification_history?userid=${u.USERID}&subprojectid=${s.SUBPROJECTID}`,
        {headers:{"Auth-Code":u.AUTH_CODE}}
      );
      const res = await r.json();
      if(res.STATUS==="1"&&Array.isArray(res.DATA)){
        const count = res.DATA.filter(n=>n.STATUS===1).length;
        setUnread(count);
        try { localStorage.setItem(cacheKey, String(count)); } catch{}
      }
    } catch{}
  };

  useEffect(()=>{
    fetchCount();
    // register listener
    const onMsg = (data) => {
      if(data.event==="new_notification") {
        setUnread(n=>{
          const next = n+1;
          try { localStorage.setItem(cacheKey, String(next)); } catch{}
          return next;
        });
        window.dispatchEvent(new CustomEvent("pvision-notification",{detail:data}));
      }
    };
    _listeners.push(onMsg);
    wsConnect(userRef.current?.USERID);
    const onVisible=()=>{ if(document.visibilityState==="visible") fetchCount(); };
    document.addEventListener("visibilitychange",onVisible);
    const onRefetch=()=>fetchCount();
    window.addEventListener("pvision-refetch-count",onRefetch);
    return()=>{
      _listeners=_listeners.filter(f=>f!==onMsg);
      document.removeEventListener("visibilitychange",onVisible);
      window.removeEventListener("pvision-refetch-count",onRefetch);
      if(_listeners.length===0) wsDisconnect();
    };
  },[]);

  const handleClose=()=>{
    setOpen(false);
    fetchCount(); // re-fetch in case user read items in the panel
  };

  return(
    <div style={{position:"relative",display:"inline-flex"}}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:"30px",height:"30px",borderRadius:"7px",
          border:`1px solid ${open?color:C.border}`,
          background:open?`${color}15`:"rgba(255,255,255,0.7)",
          display:"flex",alignItems:"center",justifyContent:"center",
          cursor:"pointer",position:"relative"}}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={open?color:C.muted} strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unread>0&&(
          <span style={{position:"absolute",top:"-5px",right:"-5px",
            minWidth:"16px",height:"16px",borderRadius:"10px",
            background:"#EF4444",color:"#fff",fontSize:"10px",fontWeight:"700",
            display:"flex",alignItems:"center",justifyContent:"center",
            padding:"0 3px",border:"2px solid #fff",lineHeight:1}}>
            {unread>99?"99+":unread}
          </span>
        )}
      </button>
      {open&&(
        <NotificationPanel user={user} activeSub={activeSub}
          color={color} onClose={handleClose}/>
      )}
    </div>
  );
}